import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import { Psychology } from '@mui/icons-material';
import { getRequest, postRequest, showMessage } from '../../../config';
import SectionAccordion from './AIPrompts/SectionAccordion';
import AnimatedPage from '../../../components/AnimatedPage';

const AIPrompts = () => {
    const [prompts, setPrompts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(true);
    const [sections, setSections] = useState([]);

    useEffect(() => {
        fetchSections();
    }, []);

    const fetchSections = async () => {
        try {
            setIsLoadingList(true);
            const response = await getRequest('/admin/repository/sections');
            if (response.code === 200) {
                setSections(response.body || []);
            } else {
                showMessage(response.message || 'Failed to fetch sections');
            }
        } catch (error) {
            console.error('Error fetching sections:', error);
            showMessage('Error fetching sections');
        } finally {
            setIsLoadingList(false);
        }
    };



    useEffect(() => {
        fetchPrompts();
    }, []);

    const fetchPrompts = async () => {
        try {
            setIsLoadingList(true);
            const response = await getRequest('/prompts');
            if (response.code === 200) {
                setPrompts(response.body || []);
            } else {
                showMessage(response.message || 'Failed to fetch prompts');
            }
        } catch (error) {
            console.error('Error fetching prompts:', error);
            showMessage('Error fetching prompts');
        } finally {
            setIsLoadingList(false);
        }
    };

    // Helper function to get prompt for a specific section
    const getPromptForSection = (sectionNo) => {
        return prompts.find(p => p.section === sectionNo);
    };

    // Handle saving a prompt (create or update)
    const handleSavePrompt = async (formData, promptId) => {
        try {
            setIsLoading(true);
            let response;

            if (promptId) {
                // Update existing prompt
                response = await postRequest(`/prompts/${promptId}`, formData);
            } else {
                // Create new prompt
                response = await postRequest('/prompts', formData);
            }

            if (response.code === 200) {
                showMessage(response.message || (promptId ? 'Prompt updated successfully' : 'Prompt created successfully'));

                // Update local state instead of refetching to preserve accordion states
                const savedPrompt = response.body;
                if (promptId) {
                    // Update existing prompt in array
                    setPrompts(prevPrompts =>
                        prevPrompts.map(p => p._id === promptId ? savedPrompt : p)
                    );
                } else {
                    // Add new prompt to array
                    setPrompts(prevPrompts => [...prevPrompts, savedPrompt]);
                }
            } else {
                showMessage(response.message || 'Failed to save prompt');
            }
        } catch (error) {
            console.error('Error saving prompt:', error);
            showMessage('Error saving prompt');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle deleting a prompt
    const handleDeletePrompt = async (promptId) => {
        try {
            setIsLoading(true);
            // Using GET request for delete as per codebase pattern
            const response = await getRequest(`/prompts/delete/${promptId}`);

            if (response.code === 200) {
                showMessage(response.message || 'Prompt deleted successfully');
                // Update local state instead of refetching to preserve accordion states
                setPrompts(prevPrompts => prevPrompts.filter(p => p._id !== promptId));
            } else {
                showMessage(response.message || 'Failed to delete prompt');
            }
        } catch (error) {
            console.error('Error deleting prompt:', error);
            showMessage('Error deleting prompt');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AnimatedPage>
            <Box className="df fdc" sx={{
                padding: { xs: '16px', sm: '24px', md: '32px' },
                minHeight: '100vh',
                background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)'
            }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 3,
                        background: 'linear-gradient(135deg, #4A67FF 0%, #3956F0 100%)',
                        borderRadius: '16px',
                        color: 'white'
                    }}
                    className="df jcsb aic"
                >
                    <Box className="df aic" sx={{ gap: 2 }}>
                        <Psychology sx={{ fontSize: 40 }} />
                        <Box>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 600,
                                    mb: 0.5,
                                    color: 'white'
                                }}
                            >
                                AI Prompt Management
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    opacity: 0.9,
                                    color: 'white'
                                }}
                            >
                                Manage and configure AI prompts for your system
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
                {isLoadingList ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 6,
                            textAlign: 'center',
                            borderRadius: '16px',
                            background: 'white'
                        }}
                        className="df fdc jcc aic"
                    >
                        <CircularProgress sx={{ color: '#4A67FF', mb: 2 }} />
                        <Typography variant="body1" color="textSecondary">
                            Loading prompts...
                        </Typography>
                    </Paper>
                ) : (
                    <Box className="df fdc" sx={{ gap: 2 }}>
                        {sections.length > 0 ? (
                            sections.map((section) => (
                                <SectionAccordion
                                    key={section._id || section.sectionNo}
                                    section={section}
                                    prompts={prompts}
                                    level={0}
                                    onSavePrompt={handleSavePrompt}
                                    onDeletePrompt={handleDeletePrompt}
                                    getPromptForSection={getPromptForSection}
                                    sections={sections}
                                    isLoading={isLoading}
                                />
                            ))
                        ) : (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 6,
                                    textAlign: 'center',
                                    borderRadius: '16px',
                                    background: 'white'
                                }}
                                className="df fdc jcc aic"
                            >
                                <Psychology sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                    No Sections Found
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Sections will appear here once they are loaded
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                )}
            </Box>
        </AnimatedPage>
    );
};

export default AIPrompts;
